import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { isEmpty, isObject } from 'lodash';
import { getStoredAccessToken } from 'pages/Auth/utils';
import { getPostHogTrackingHeaders } from 'scalarAnalytics/postHog';
import { Logger } from 'utilities';
import { memoizedRefreshToken } from '../refreshToken';
import { emitThrottleEnd, emitThrottleStart } from '../throttleSignal';
import { IInterceptorConfigRequest } from '../types';

const API_DEFAULT_MESSAGE_REQUEST = 'The request is invalid';

// 429 retry budget. DRF's UserRateThrottle uses a 60s sliding window, so a
// burst that exhausts the bucket can keep returning 429 for up to a minute
// before the oldest requests age out. The budget needs to span that worst
// case; otherwise retries exhaust early, the iframe falls idle, and the PDF
// export pipeline's 30s stall window kills it.
const MAX_THROTTLE_RETRIES = 8;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 15_000;

// Heartbeat cadence used while sleeping between retries. The PDF export
// pipeline's `waitForReadiness` kills any iframe that goes 30s without DOM
// mutations while its export status is still "loading"; we pulse a body
// attribute every few seconds so a long Retry-After wait doesn't trip that
// stall window.
const KEEPALIVE_INTERVAL_MS = 5_000;
const KEEPALIVE_BODY_ATTR = 'data-throttle-keepalive';

const getAccessToken = () => getStoredAccessToken();

// axios v1 wraps headers in AxiosHeaders; index access works most of the time
// but `.get('Retry-After')` is the safer path (it normalizes case and handles
// the AxiosHeaders internal storage). We probe both and accept arrays/strings/
// numbers so DRF's integer-second response and HTTP-date responses both parse.
const readRetryAfterHeader = (response: AxiosError['response']): string | null => {
  const headers = response?.headers as
    | { get?: (name: string) => unknown; [key: string]: unknown }
    | undefined;
  if (!headers) {
    return null;
  }
  let raw: unknown;
  if (typeof headers.get === 'function') {
    raw = headers.get('Retry-After') ?? headers.get('retry-after');
  }
  if (raw == null) {
    raw = headers['retry-after'] ?? headers['Retry-After'];
  }
  if (Array.isArray(raw)) {
    [raw] = raw;
  }
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? String(raw) : null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || null;
  }
  return null;
};

const parseRetryAfterMs = (header: string | null): number | null => {
  if (!header) {
    return null;
  }
  const seconds = Number(header);
  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.round(seconds * 1000));
  }
  const dateMs = Date.parse(header);
  if (Number.isFinite(dateMs)) {
    return Math.max(0, dateMs - Date.now());
  }
  return null;
};

const computeBackoffMs = (attempt: number): number => {
  const expo = BASE_BACKOFF_MS * 2 ** Math.max(0, attempt - 1);
  return Math.min(MAX_BACKOFF_MS, expo);
};

// Jitter spreads retries so a wave of throttled requests doesn't re-flood the
// server in lock-step when the Retry-After window expires.
const applyJitter = (delayMs: number): number => {
  const jitter = 0.85 + Math.random() * 0.3;
  return Math.round(delayMs * jitter);
};

const sleep = (ms: number) => new Promise<void>(resolve => {
  setTimeout(resolve, ms);
});

const pulseKeepalive = () => {
  if (typeof document !== 'undefined' && document.body) {
    document.body.setAttribute(KEEPALIVE_BODY_ATTR, String(Date.now()));
  }
};

// Sleeps in chunks and pulses a body attribute between chunks. Each pulse is a
// DOM mutation that resets the parent's stall-recovery timer for the export
// iframe; outside iframes this is harmless extra writes to body.dataset.
const sleepWithKeepalive = async (totalMs: number) => {
  // Immediate pulse so a request that hits 429 right at the stall-window edge
  // resets the parent timer before we start the long wait.
  pulseKeepalive();
  const start = Date.now();
  while (true) {
    const elapsed = Date.now() - start;
    const remaining = totalMs - elapsed;
    if (remaining <= 0) {
      return;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(Math.min(KEEPALIVE_INTERVAL_MS, remaining));
    pulseKeepalive();
  }
};

const handleError = (serverError: unknown) => {
  if (isObject(serverError)) {
    Object.entries(serverError).forEach(([, value]) => {
      const errorMessage = isEmpty(value) ? API_DEFAULT_MESSAGE_REQUEST : value;
      // eslint-disable-next-line no-console
      console.error(`${errorMessage}`);
    });
  }
};

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const accessToken = getAccessToken();

  if (accessToken) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Tag the request with the current PostHog session/person so backend events link to the replay + user.
  Object.entries(getPostHogTrackingHeaders()).forEach(([key, value]) => {
    // eslint-disable-next-line no-param-reassign
    config.headers[key] = value;
  });

  return config;
};

const buildOnResponseError = (axiosInstance: AxiosInstance) =>
  async (error: AxiosError): Promise<AxiosError> => {
    let config: IInterceptorConfigRequest | undefined = error?.config;

    if (error?.response?.status === 401 && !config?.sent) {
      config = {
        ...config,
        sent: true,
      };

      const result = await memoizedRefreshToken();

      if (result?.access) {
        const token = `Bearer ${result.access}`;

        config.headers = {
          ...config.headers,
          Authorization: token,
        };

        return axios(config);
      }
    }

    if (error?.response?.status === 429 && config) {
      const attempts = config.throttleRetryCount ?? 0;
      if (attempts < MAX_THROTTLE_RETRIES) {
        const retryAfterHeader = readRetryAfterHeader(error.response);
        const retryAfterMs = parseRetryAfterMs(retryAfterHeader);
        const delayMs = applyJitter(retryAfterMs ?? computeBackoffMs(attempts + 1));
        const nextConfig: IInterceptorConfigRequest = {
          ...config,
          throttleRetryCount: attempts + 1,
        };

        Logger.info(
          `[throttle] 429 on ${config.method?.toUpperCase() ?? 'REQ'} ${config.url} — retry ${attempts + 1}/${MAX_THROTTLE_RETRIES} in ${delayMs}ms`
        );
        emitThrottleStart();
        try {
          await sleepWithKeepalive(delayMs);
          return await axiosInstance(nextConfig);
        } finally {
          emitThrottleEnd();
        }
      }
      Logger.warn(`[throttle] 429 retry budget exhausted for ${config.url}`);
    }

    handleError(error?.response?.data);

    return Promise.reject(error);
  };

const setupInterceptorsTo = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, undefined);
  axiosInstance.interceptors.response.use(undefined, buildOnResponseError(axiosInstance));

  return axiosInstance;
};

export default setupInterceptorsTo;
